import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  Monitor,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  Filter,
  Download,
  Eye,
  UserCheck,
  Wrench,
  RotateCcw,
  X
} from "lucide-react";

const ESTADOS = [
  { value: "disponible", label: "Disponible", color: "bg-green-100 text-green-700" },
  { value: "asignado", label: "Asignado", color: "bg-purple-100 text-purple-700" },
  { value: "mantenimiento", label: "En Mantenimiento", color: "bg-amber-100 text-amber-700" },
  { value: "baja", label: "Dado de Baja", color: "bg-red-100 text-red-700" }
];

export default function MobiliarioActivosPage() {
  const [activos, setActivos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [ubicaciones, setUbicaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("all");
  const [filterEstado, setFilterEstado] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editingActivo, setEditingActivo] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    codigo: "",
    nombre: "",
    descripcion: "",
    categoria_id: "",
    ubicacion_id: "",
    marca: "",
    modelo: "",
    numero_serie: "",
    fecha_adquisicion: "",
    valor_adquisicion: 0,
    moneda: "RD$",
    estado: "disponible",
    proveedor: "",
    garantia_hasta: "",
    notas: ""
  });
  
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const [activosRes, categoriasRes, ubicacionesRes] = await Promise.all([
        axios.get(`${API}/mobiliario/activos`, getAuthHeaders()),
        axios.get(`${API}/mobiliario/categorias`, getAuthHeaders()),
        axios.get(`${API}/mobiliario/ubicaciones`, getAuthHeaders())
      ]);
      setActivos(activosRes.data || []);
      setCategorias(categoriasRes.data || []);
      setUbicaciones(ubicacionesRes.data || []);
    } catch (err) {
      console.error("Error loading data:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredActivos = activos.filter(a => {
    const matchesSearch = !searchTerm || 
      a.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.marca?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.numero_serie?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategoria = filterCategoria === "all" || a.categoria_id === filterCategoria;
    const matchesEstado = filterEstado === "all" || a.estado === filterEstado;
    return matchesSearch && matchesCategoria && matchesEstado;
  });

  const openNewDialog = () => {
    setEditingActivo(null);
    setFormData({
      codigo: "",
      nombre: "",
      descripcion: "",
      categoria_id: "",
      ubicacion_id: "",
      marca: "",
      modelo: "",
      numero_serie: "",
      fecha_adquisicion: new Date().toISOString().split('T')[0],
      valor_adquisicion: 0,
      moneda: "RD$",
      estado: "disponible",
      proveedor: "",
      garantia_hasta: "",
      notas: ""
    });
    setShowDialog(true);
  };

  const openEditDialog = (activo) => {
    setEditingActivo(activo);
    setFormData({
      codigo: activo.codigo || "",
      nombre: activo.nombre || "",
      descripcion: activo.descripcion || "",
      categoria_id: activo.categoria_id || "",
      ubicacion_id: activo.ubicacion_id || "",
      marca: activo.marca || "",
      modelo: activo.modelo || "",
      numero_serie: activo.numero_serie || "",
      fecha_adquisicion: activo.fecha_adquisicion || "",
      valor_adquisicion: activo.valor_adquisicion || 0,
      moneda: activo.moneda || "RD$",
      estado: activo.estado || "disponible",
      proveedor: activo.proveedor || "",
      garantia_hasta: activo.garantia_hasta || "",
      notas: activo.notas || ""
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.codigo || !formData.nombre) {
      toast.error("Código y nombre son requeridos");
      return;
    }

    setSaving(true);
    try {
      if (editingActivo) {
        await axios.put(`${API}/mobiliario/activos/${editingActivo.id}`, formData, getAuthHeaders());
        toast.success("Activo actualizado");
      } else {
        await axios.post(`${API}/mobiliario/activos`, formData, getAuthHeaders());
        toast.success("Activo creado");
      }
      setShowDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Está seguro de eliminar este activo?")) return;
    
    try {
      await axios.delete(`${API}/mobiliario/activos/${id}`, getAuthHeaders());
      toast.success("Activo eliminado");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  // Selection functions
  const toggleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === filteredActivos.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredActivos.map(item => item.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      toast.error("Seleccione al menos un activo");
      return;
    }
    
    if (!window.confirm(`¿Eliminar ${selectedItems.length} activo(s) seleccionado(s)?`)) return;
    
    try {
      setBulkActionLoading(true);
      await axios.post(`${API}/mobiliario/activos/bulk-delete`, { activo_ids: selectedItems }, getAuthHeaders());
      toast.success(`${selectedItems.length} activo(s) eliminado(s)`);
      setSelectedItems([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar activos");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleResetValores = async () => {
    if (!window.confirm(`¿Resetear valores a 0 para TODOS los activos?`)) return;
    
    try {
      setBulkActionLoading(true);
      await axios.post(`${API}/mobiliario/activos/reset-valores`, {}, getAuthHeaders());
      toast.success(`Valores reseteados a 0`);
      setSelectedItems([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al resetear valores");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/mobiliario/export/activos`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'activos_mobiliario.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Exportación completada");
    } catch (err) {
      toast.error("Error al exportar");
    }
  };

  const getEstadoBadge = (estado) => {
    const config = ESTADOS.find(e => e.value === estado) || ESTADOS[0];
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="mobiliario-activos-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Activos de Mobiliario
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {activos.length} activos registrados
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
            <Button onClick={openNewDialog} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Activo
            </Button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedItems.length > 0 && (
          <Card className="mb-4 border-blue-200 bg-blue-50">
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <span className="text-blue-700 font-medium">
                  {selectedItems.length} activo(s) seleccionado(s)
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    data-testid="bulk-delete-activos-btn"
                  >
                    {bulkActionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Eliminar Seleccionados
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedItems([])}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Global Reset Button */}
        <div className="flex justify-end mb-4">
          <Button
            variant="outline"
            onClick={handleResetValores}
            disabled={bulkActionLoading || activos.length === 0}
            className="text-amber-600 border-amber-300 hover:bg-amber-50"
            data-testid="reset-all-valores-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Resetear Valores de Todos ({activos.length})
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por código, nombre, marca..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas las categorías" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {categorias.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterEstado} onValueChange={setFilterEstado}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos los estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {ESTADOS.map(e => (
                    <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                variant="ghost" 
                onClick={() => { setSearchTerm(""); setFilterCategoria("all"); setFilterEstado("all"); }}
              >
                Limpiar filtros
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Activos Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-slate-50 dark:bg-slate-800">
                    <th className="w-12 p-4">
                      <Checkbox
                        checked={selectedItems.length === filteredActivos.length && filteredActivos.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-activos-checkbox"
                      />
                    </th>
                    <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Código</th>
                    <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Nombre</th>
                    <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Categoría</th>
                    <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Ubicación</th>
                    <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Estado</th>
                    <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Valor</th>
                    <th className="text-right p-4 font-medium text-slate-600 dark:text-slate-300">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActivos.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="p-8 text-center text-slate-500">
                        No se encontraron activos
                      </td>
                    </tr>
                  ) : (
                    filteredActivos.map((activo) => (
                      <tr key={activo.id} className={`border-b hover:bg-slate-50 dark:hover:bg-slate-800/50 ${selectedItems.includes(activo.id) ? "bg-blue-50" : ""}`}>
                        <td className="p-4">
                          <Checkbox
                            checked={selectedItems.includes(activo.id)}
                            onCheckedChange={() => toggleSelectItem(activo.id)}
                            data-testid={`select-activo-${activo.id}`}
                          />
                        </td>
                        <td className="p-4">
                          <span className="font-mono text-sm font-medium text-slate-900 dark:text-white">
                            {activo.codigo}
                          </span>
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">{activo.nombre}</p>
                            {activo.marca && (
                              <p className="text-sm text-slate-500">{activo.marca} {activo.modelo}</p>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          {activo.categoria_nombre && (
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-2 h-2 rounded-full" 
                                style={{ backgroundColor: activo.categoria_color || "#6b7280" }}
                              />
                              <span className="text-slate-600 dark:text-slate-300">{activo.categoria_nombre}</span>
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-slate-600 dark:text-slate-300">
                          {activo.ubicacion_nombre || "-"}
                        </td>
                        <td className="p-4">
                          {getEstadoBadge(activo.estado)}
                          {activo.asignado_a_nombre && (
                            <p className="text-xs text-slate-500 mt-1">→ {activo.asignado_a_nombre}</p>
                          )}
                        </td>
                        <td className="p-4 text-slate-600 dark:text-slate-300">
                          {activo.moneda} {activo.valor_adquisicion?.toLocaleString()}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(activo)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            {activo.estado !== "asignado" && activo.estado !== "baja" && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDelete(activo.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Monitor className="w-5 h-5 text-[#0C314C]" />
                {editingActivo ? "Editar Activo" : "Nuevo Activo"}
              </DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Código *</Label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                  placeholder="MOB-001"
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  placeholder="Escritorio ejecutivo"
                />
              </div>

              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select 
                  value={formData.categoria_id} 
                  onValueChange={(v) => setFormData({...formData, categoria_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categorias.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ubicación</Label>
                <Select 
                  value={formData.ubicacion_id} 
                  onValueChange={(v) => setFormData({...formData, ubicacion_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ubicaciones.map(ub => (
                      <SelectItem key={ub.id} value={ub.id}>{ub.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Marca</Label>
                <Input
                  value={formData.marca}
                  onChange={(e) => setFormData({...formData, marca: e.target.value})}
                  placeholder="Marca del activo"
                />
              </div>
              <div className="space-y-2">
                <Label>Modelo</Label>
                <Input
                  value={formData.modelo}
                  onChange={(e) => setFormData({...formData, modelo: e.target.value})}
                  placeholder="Modelo"
                />
              </div>

              <div className="space-y-2">
                <Label>Número de Serie</Label>
                <Input
                  value={formData.numero_serie}
                  onChange={(e) => setFormData({...formData, numero_serie: e.target.value})}
                  placeholder="SN-XXXXX"
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha de Adquisición</Label>
                <Input
                  type="date"
                  value={formData.fecha_adquisicion}
                  onChange={(e) => setFormData({...formData, fecha_adquisicion: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>Valor de Adquisición</Label>
                <Input
                  type="number"
                  value={formData.valor_adquisicion}
                  onChange={(e) => setFormData({...formData, valor_adquisicion: parseFloat(e.target.value) || 0})}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Proveedor</Label>
                <Input
                  value={formData.proveedor}
                  onChange={(e) => setFormData({...formData, proveedor: e.target.value})}
                  placeholder="Nombre del proveedor"
                />
              </div>

              <div className="space-y-2">
                <Label>Garantía hasta</Label>
                <Input
                  type="date"
                  value={formData.garantia_hasta}
                  onChange={(e) => setFormData({...formData, garantia_hasta: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select 
                  value={formData.estado} 
                  onValueChange={(v) => setFormData({...formData, estado: v})}
                  disabled={editingActivo?.estado === "asignado"}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTADOS.filter(e => e.value !== "asignado" && e.value !== "baja").map(e => (
                      <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2 space-y-2">
                <Label>Descripción / Notas</Label>
                <Textarea
                  value={formData.notas}
                  onChange={(e) => setFormData({...formData, notas: e.target.value})}
                  placeholder="Notas adicionales..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button 
                onClick={handleSubmit} 
                disabled={saving}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingActivo ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
